import { cn } from "@qn/ui";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import { type AgentInfo, useAgents } from "../lib/agents";
import { useT } from "../lib/i18n";
import { NamedIcon } from "./NamedIcon";

export function AgentPicker({
  value,
  onPick,
}: {
  value: string | null;
  onPick: (id: string | null) => void;
}) {
  const t = useT();
  const agents = useAgents().data?.agents ?? [];
  const current = agents.find((a) => a.id === (value ?? "builtin:orchestrator")) ?? agents[0];
  const item =
    "flex cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none data-[highlighted]:bg-[var(--surface-3)]";
  const row = (a: AgentInfo) => (
    <Dropdown.Item
      key={a.id}
      onSelect={() => onPick(a.id === "builtin:orchestrator" ? null : a.id)}
      className={item}
    >
      <NamedIcon name={a.icon} />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium" dir="auto">
          {a.name}
        </span>
        <span className="block truncate text-xs text-[var(--fg-faint)]" dir="auto">
          {a.description}
        </span>
      </span>
      <Check
        className={cn(
          "mt-0.5 size-4 text-[var(--accent-text)]",
          current?.id === a.id ? "opacity-100" : "opacity-0",
        )}
      />
    </Dropdown.Item>
  );
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button
          type="button"
          aria-label={t("agents.pick")}
          className="flex max-w-[40vw] items-center gap-1.5 rounded-xl border border-[var(--line)] bg-[var(--surface)] px-2.5 py-1.5 text-sm transition hover:border-[var(--line-strong)] hover:bg-[var(--surface-2)]"
        >
          <NamedIcon
            name={current?.icon}
            fallback="sparkles"
            tile={false}
            className="size-[18px] text-[var(--accent-text)]"
          />
          <span className="hidden truncate font-medium sm:inline" dir="auto">
            {current?.name ?? "General"}
          </span>
          <ChevronDown className="size-4 shrink-0 text-[var(--fg-faint)]" />
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          align="start"
          sideOffset={6}
          className="glass z-50 max-h-[70dvh] w-80 overflow-y-auto rounded-xl border border-[var(--line)] p-1.5 shadow-[var(--shadow)]"
        >
          {agents.filter((a) => a.builtin).map(row)}
          {agents.some((a) => !a.builtin) && (
            <Dropdown.Label className="px-2.5 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-[var(--fg-faint)]">
              {t("agents.mine")}
            </Dropdown.Label>
          )}
          {agents.filter((a) => !a.builtin).map(row)}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
